import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import ChatInput from './ChatInput';
import LoadingSpinner from './LoadingSpinner';
import PdfPreviewModal from './PdfPreviewModal';
import { processQuoteChatMessage } from '../lib/quoteAiService';
import { UserProfile, FinancialConfig, Material, ProductTemplate, QuoteProductItem, SimpleService } from '../types';
import { AlertCircle } from 'lucide-react';
import { parseMarkdownToReact } from '../utils/markdownUtils';
import { compressImage } from '../utils/imageUtils';

export interface AIMatchedService {
  name: string;
  value: number;
  quantity?: number;
  unitValue?: number;
  isUnitBased?: boolean;
  unitLabel?: string;
}

export interface AIMatchedProduct {
  name: string;
  unitValue: number;
  quantity: number;
  total?: number;
}

export interface ChatAssistantProps {
  user: UserProfile;
  config: FinancialConfig;
  materials: Material[];
  recipes: ProductTemplate[];
  services?: SimpleService[];
  initialFormData?: Partial<any>;
  quotesCount: number;
  plan: 'Grátis' | 'Pro' | 'Especialista' | 'Empresa' | 'Empresarial' | 'Enterprise';
  onFormDataUpdate: (patch: Partial<Record<string, any>>) => void;
  onAiExplanation?: (explanation: string) => void;
  onMessagesChange?: (messages: { id: string; role: 'user' | 'assistant'; content: string }[]) => void;
  onBudgetReady?: (ready: boolean) => void;
  onSaveDraft?: (draft: { messages?: any[]; form?: any }) => void;
  onOpenProfile?: (step?: any) => void;
  onOpenCosts?: () => void;
  onOpenHistory?: () => void;
  onClose?: () => void;
}

interface AIResponse {
  updatedData?: {
    clientName?: string | null;
    serviceName?: string | null;
    serviceDescription?: string | null;
    materialCost?: number | null;
    timeSpentMinutes?: number | null;
    finalPrice?: number | null;
    matchedServices?: AIMatchedService[];
    matchedProducts?: AIMatchedProduct[];
  };
  explanation?: string;
  reply?: string;
  isComplete?: boolean;
}

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  type?: 'text' | 'image' | 'audio';
  imageUrl?: string;
}

export default function ChatAssistant({
  user,
  config,
  materials,
  recipes,
  services = [],
  initialFormData,
  quotesCount,
  plan,
  onFormDataUpdate,
  onAiExplanation,
  onMessagesChange,
  onBudgetReady,
  onOpenProfile,
  onOpenCosts,
  onOpenHistory,
}: ChatAssistantProps) {
  const hasCreatedQuote = useMemo(() => {
    return quotesCount > 0 || localStorage.getItem('@rmax:hasCreatedQuote') === 'true' || localStorage.getItem('rmax_has_saved_quote') === 'true';
  }, [quotesCount]);

  const [messages, setMessages] = useState<Message[]>(() => {
    const isNew = quotesCount === 0 && 
                  localStorage.getItem('@rmax:hasCreatedQuote') !== 'true' && 
                  localStorage.getItem('rmax_has_saved_quote') !== 'true';
    const defaultInit = [{
      id: '1',
      role: 'assistant',
      content: isNew 
        ? `Olá! 👋 Bem-vindo ao R-Max. Para começarmos, me diga: o que você precisa orçar hoje?`
        : `Oi, ${user.ownerName.split(' ')[0]}! O que vamos vender hoje? Pode escrever, mandar áudio ou foto da lista.`
    }];
    try {
      if (typeof window === 'undefined') return defaultInit;
      const saved = localStorage.getItem(`rmax_draft_messages_${user.id}`);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.every(m => m && typeof m === 'object' && 'id' in m && 'role' in m)) {
          return parsed;
        }
        localStorage.removeItem(`rmax_draft_messages_${user.id}`);
      }
      return defaultInit;
    } catch (e) {
      console.error('Error parsing draft messages:', e);
      try {
        localStorage.removeItem(`rmax_draft_messages_${user.id}`);
      } catch {}
      return defaultInit;
    }
  });

  const [inputValue, setInputValue] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingSecondsLeft, setRecordingSecondsLeft] = useState(60);
  const recognitionRef = useRef<any>(null);
  const recordingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [networkError, setNetworkError] = useState<string | null>(null);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);

  const [loadingMessage, setLoadingMessage] = useState('R-Max está pensando...');

  const loadingMessages = useMemo(() => [
    "Dando os retoques finais no orçamento...",
    "Validando a precificação inteligente...",
    "Otimizando a margem de segurança...",
    "Afinando a estratégia de preços...",
    "Consolidando os dados comerciais...",
    "Calculando margens de lucro...",
    "Refinando os custos e impostos...",
    "Analisando contexto do serviço...",
    "Estruturando os dados do orçamento...",
    "Quase pronto para você revisar..."
  ], []);

  useEffect(() => {
    let interval: any;
    if (isProcessing) {
      let index = Math.floor(Math.random() * loadingMessages.length);
      setLoadingMessage(loadingMessages[index]);
      interval = setInterval(() => {
        index = (index + 1) % loadingMessages.length;
        setLoadingMessage(loadingMessages[index]);
      }, 3000);
    }
    return () => clearInterval(interval);
  }, [isProcessing, loadingMessages]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      if (messages.length > 1) {
        localStorage.setItem(`rmax_draft_messages_${user.id}`, JSON.stringify(messages.map(m => ({ ...m, imageUrl: undefined }))));
      }
      onMessagesChange?.(messages.map(m => ({ id: m.id, role: m.role, content: m.content })));
    } catch (e) {
      console.error('Error saving messages draft:', e);
    }
  }, [messages, user.id, onMessagesChange]);

  const handleSendMessage = useCallback(async (text: string, imageBase64?: string) => {
    if ((!text.trim() && !imageBase64) || isProcessing) return;

    const userMsg: Message = {
      id: crypto.randomUUID(),
      role: 'user',
      content: text,
      type: imageBase64 ? 'image' : 'text',
      imageUrl: imageBase64 ? `data:image/jpeg;base64,${imageBase64}` : undefined
    };

    setMessages(prev => [...prev, userMsg]);
    setInputValue('');
    setIsProcessing(true);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 25000);

    try {
      const output = await processQuoteChatMessage({
        text,
        imageBase64,
        history: messages.map(m => ({ role: m.role, content: m.content })),
        currentCart: {
          clientName: initialFormData?.clientName,
          products: initialFormData?.products,
          services: initialFormData?.services
        },
        user,
        config,
        catalogs: { services, recipes, materials },
        signal: controller.signal
      });

      // Emit form patch to parent
      // s.value enviado como unitValue para garantir que QuoteCreator (s.value * s.quantity) calcule o subtotal exato
      onFormDataUpdate({
        clientName: output.clientName,
        serviceName: output.serviceName || (output.products.length > 0 && output.services.length === 0 ? 'Venda de Produtos' : null),
        services: output.services,
        matchedServices: output.services.map(s => ({
          name: s.name,
          quantity: s.quantity,
          unitValue: s.unitValue,
          value: s.unitValue,
          isUnitBased: s.isUnitBased,
          unitLabel: s.unitLabel
        })),
        matchedProducts: output.products,
        finalPrice: output.itemsSum > 0 ? output.itemsSum : null
      });

      if (output.explanation) onAiExplanation?.(output.explanation);

      // Balão estruturado com a resposta conversacional + resumo limpo dos itens
      const assistantBubbleText = output.itemsSummaryText
        ? `${output.reply}\n\n📋 **Itens na Proposta:**\n${output.itemsSummaryText}\n\n**Total dos Itens:** R$ ${output.itemsSum.toFixed(2).replace('.', ',')}`
        : output.reply;

      setMessages(prev => [...prev, { id: crypto.randomUUID(), role: 'assistant', content: assistantBubbleText }]);
      
      const hasItemsInProposal = output.itemsSum > 0 || (output.services && output.services.length > 0) || (output.products && output.products.length > 0);
      if (hasItemsInProposal) {
        setTimeout(() => onBudgetReady?.(true), 600);
      }
    } catch (error: any) {
      console.error('AI Error:', error);
      const errorMsg = error.name === 'AbortError' 
        ? 'A conexão demorou um pouquinho. Verifique seu sinal e tente novamente!' 
        : 'O leitor automático está em manutenção rápida. Escreva o que você precisa orçar que calculamos agora mesmo!';
      setNetworkError(errorMsg);
      setTimeout(() => setNetworkError(null), 7000);
    } finally {
      clearTimeout(timeoutId);
      setIsProcessing(false);
    }
  }, [isProcessing, materials, recipes, services, initialFormData, user, config, onFormDataUpdate, onAiExplanation, onBudgetReady, messages]);

  const handleApplyDeadline = useCallback(() => {
    onFormDataUpdate({ deadline: '15 dias úteis' });
    setMessages(prev => [
      ...prev,
      {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: '✓ Prazo de entrega fixado em **15 dias úteis** no orçamento.'
      }
    ]);
  }, [onFormDataUpdate]);

  const handleApplyPix = useCallback(() => {
    onFormDataUpdate({ paymentMethod: 'PIX', paymentTerms: 'À vista via PIX' });
    setMessages(prev => [
      ...prev,
      {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: '✓ Condição comercial atualizada: **À vista via PIX** (Chave PIX inserida automaticamente no PDF).'
      }
    ]);
  }, [onFormDataUpdate]);

  const handleOpenPreview = useCallback(() => {
    onBudgetReady?.(true);
    setIsPreviewModalOpen(true);
  }, [onBudgetReady]);

  const handleImageUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const compressedDataUrl = await compressImage(file, { purpose: 'ocr' });
      const base64 = compressedDataUrl.split(',')[1];
      await handleSendMessage('Imagem enviada', base64);
    } catch (err: any) {
      console.error('Erro ao comprimir imagem:', err);
      alert(err.message || 'Erro ao processar imagem.');
    }
  }, [handleSendMessage]);

  const cleanupRecording = useCallback(() => {
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    setIsRecording(false);
    setRecordingSecondsLeft(60);
  }, []);

  const handleMicClick = useCallback(() => {
    if (isRecording) {
      try {
        recognitionRef.current?.stop();
      } catch (e) {
        console.warn('Erro ao interromper áudio manualmente:', e);
      }
      cleanupRecording();
      return;
    }

    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) return alert('Seu dispositivo ou navegador não suporta entrada de áudio.');

    try {
      const recognition = new SpeechRecognition();
      recognition.lang = 'pt-BR';
      recognition.continuous = false;
      recognition.interimResults = false;
      recognitionRef.current = recognition;

      recognition.onstart = () => {
        setIsRecording(true);
        setRecordingSecondsLeft(60);
        if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);

        let secondsRemaining = 60;
        recordingTimerRef.current = setInterval(() => {
          secondsRemaining -= 1;
          setRecordingSecondsLeft(secondsRemaining);
          if (secondsRemaining <= 0) {
            try {
              recognition.stop();
            } catch (e) {
              console.warn('Erro ao cortar áudio no limite de 60s:', e);
            }
            cleanupRecording();
          }
        }, 1000);
      };

      recognition.onresult = (e: any) => {
        const transcript = e.results?.[0]?.[0]?.transcript;
        if (transcript) {
          handleSendMessage(transcript);
        }
      };

      recognition.onerror = (err: any) => {
        console.warn('Erro no reconhecimento de voz:', err);
        cleanupRecording();
      };

      recognition.onend = () => {
        cleanupRecording();
      };

      recognition.start();
    } catch (err) {
      console.error('Falha ao inicializar gravação:', err);
      cleanupRecording();
    }
  }, [isRecording, cleanupRecording, handleSendMessage]);

  useEffect(() => {
    return () => {
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
    };
  }, []);

  return (
    <div className="lg:col-span-7 flex flex-col space-y-4 min-h-[40vh] lg:min-h-full pb-8 lg:pb-8">
      <AnimatePresence initial={false}>
        {messages.map((msg, idx) => (
          <React.Fragment key={msg.id}>
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} mb-3`}>
              <div className={`max-w-[85%] rounded-2xl p-3 shadow-sm ${msg.role === 'user' ? 'bg-[#E3F2FD] text-zinc-900 list-none' : 'bg-white text-slate-900'}`}>
                {msg.imageUrl && <img src={msg.imageUrl} className="rounded-lg mb-2 max-h-48 whitespace-pre-wrap" alt="Upload" />}
                <div className="text-sm space-y-1 my-0.5 leading-relaxed">
                  {parseMarkdownToReact(msg.content)}
                </div>
                {idx === messages.length - 1 && msg.role === 'assistant' && !isProcessing && ((initialFormData?.services && initialFormData.services.length > 0) || (initialFormData?.products && initialFormData.products.length > 0) || (initialFormData?.finalPrice && initialFormData.finalPrice > 0)) && (
                  <div className="flex flex-wrap gap-1.5 mt-3 pt-2.5 border-t border-slate-100">
                    <button
                      type="button"
                      onClick={handleOpenPreview}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-black bg-zinc-900 hover:bg-zinc-800 text-white shadow-xs transition-all active:scale-95 cursor-pointer"
                    >
                      <span>⚡ Ver Orçamento Completo</span>
                      {initialFormData?.finalPrice ? <span className="text-amber-300 font-mono">(R$ {Number(initialFormData.finalPrice).toFixed(2).replace('.', ',')})</span> : null}
                    </button>
                    <button
                      type="button"
                      onClick={handleApplyDeadline}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-medium bg-slate-50 hover:bg-slate-100 border border-slate-200 text-slate-700 transition-colors cursor-pointer"
                    >
                      + Prazo 15 dias
                    </button>
                    <button
                      type="button"
                      onClick={handleApplyPix}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-medium bg-slate-50 hover:bg-slate-100 border border-slate-200 text-slate-700 transition-colors cursor-pointer"
                    >
                      + PIX à vista
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
            {idx === 0 && messages.length <= 1 && !isProcessing && !hasCreatedQuote && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-col gap-2 mt-1 mb-4 ml-2">
                <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest ml-2">Experimente pedir assim:</p>
                <div className="flex flex-wrap gap-2">
                  <button onClick={() => handleSendMessage("Vender um bolo de cenoura por 40 reais")} className="bg-white border border-brand/20 text-brand px-3 py-2 rounded-xl text-xs font-bold shadow-sm hover:bg-brand/5 transition-colors text-left leading-tight">
                    "Vender um bolo de cenoura por 40 reais"
                  </button>
                  <button onClick={() => handleSendMessage("Troca de tela iPhone 13, gastei 150 na tela e demorei 1h")} className="bg-white border border-brand/20 text-brand px-3 py-2 rounded-xl text-xs font-bold shadow-sm hover:bg-brand/5 transition-colors text-left leading-tight">
                    "Troca de tela iPhone 13, gastei 150 na tela e demorei 1h"
                  </button>
                  <button onClick={() => handleSendMessage("Fiz uma faxina. Foram 4 horas de trabalho. Cobrarei 180")} className="bg-white border border-brand/20 text-brand px-3 py-2 rounded-xl text-xs font-bold shadow-sm hover:bg-brand/5 transition-colors text-left leading-tight">
                    "Fiz uma faxina. Foram 4 horas de trabalho. Cobrarei 180"
                  </button>
                </div>
              </motion.div>
            )}
          </React.Fragment>
        ))}
      </AnimatePresence>

      {isProcessing && <div className="text-zinc-400 text-xs animate-pulse pb-4">{loadingMessage}</div>}

      {networkError && (
        <div className="bg-rose-50 border border-rose-200 text-rose-600 p-3 rounded-xl text-xs font-bold flex items-center gap-2 shadow-sm">
          <AlertCircle className="h-4 w-4 text-rose-600" />
          {networkError}
        </div>
      )}

      {Boolean(initialFormData?.finalPrice && initialFormData.finalPrice > 0) && !isProcessing && (
        <div className="lg:hidden mb-2 px-3 py-2 bg-zinc-900 text-white rounded-2xl flex items-center justify-between shadow-md">
          <div className="flex items-center gap-1.5">
            <span className="text-amber-400 font-bold">⚡</span>
            <span className="text-xs font-bold">
              Orçamento: R$ {Number(initialFormData?.finalPrice || 0).toFixed(2).replace('.', ',')}
            </span>
          </div>
          <button
            type="button"
            onClick={handleOpenPreview}
            className="text-[11px] font-black bg-white text-zinc-900 px-3 py-1 rounded-xl shadow-xs active:scale-95 transition-all cursor-pointer"
          >
            Ver Orçamento
          </button>
        </div>
      )}

      <div className="mt-auto">
        <ChatInput
          inputValue={inputValue}
          setInputValue={setInputValue}
          isProcessing={isProcessing}
          isRecording={isRecording}
          recordingSecondsLeft={recordingSecondsLeft}
          handleSendMessage={handleSendMessage}
          handleMicClick={handleMicClick}
          handleImageUpload={handleImageUpload}
        />
      </div>

      {isPreviewModalOpen && (
        <PdfPreviewModal
          isOpen={isPreviewModalOpen}
          onClose={() => setIsPreviewModalOpen(false)}
          user={user}
          formData={initialFormData}
          finalPrice={initialFormData?.finalPrice || 0}
          quoteCount={quotesCount}
          isProUser={plan !== 'Grátis'}
          onDownloadPDF={() => {
            setIsPreviewModalOpen(false);
            onBudgetReady?.(true);
          }}
        />
      )}
    </div>
  );
}
